import React, { memo, useMemo } from 'react'
import { isNil } from 'lodash'
import ClipboardCopy from 'transfix-ui/components/Copy'
import { Grid, makeStyles, Typography } from 'transfix-ui/core'
import Skeleton from 'transfix-ui/core/Skeleton'
import { DISPLAY } from 'constants/display'

const useSidebarListDataStyles = makeStyles({
  clipboardCopyContainer: {
    marginLeft: 2,
  },
  header: {
    display: 'inline-flex',
    marginBottom: 5,
  },
  list: {
    maxWidth: 300,
  },
})

export interface ISidebarListDataItem {
  label: string
  value?: string | number | JSX.Element | null
  dataTest?: string
}

export interface ISidebarListDataProps {
  canCopy?: boolean
  label: string
  listData: ISidebarListDataItem[]
  loading: boolean
}

export const SidebarListData = memo<ISidebarListDataProps>(
  ({ canCopy, label, listData, loading }) => {
    const classes = useSidebarListDataStyles()

    const clipboardContent = useMemo(() => {
      if (canCopy) {
        return listData
          .map(({ label, value }) => {
            const formattedValue = isNil(value) ? DISPLAY.empty : value

            return `${label}: ${formattedValue}`
          })
          .join('\n')
      }

      return null
    }, [canCopy, listData])

    return (
      <div>
        <div className={classes.header}>
          <Typography variant='subtitle1' className='text-secondary'>
            {label}
          </Typography>

          {clipboardContent && (
            <div className={classes.clipboardCopyContainer}>
              <ClipboardCopy
                alignClass='left'
                useIcon
                copyText={clipboardContent}
                displayText={label}
              />
            </div>
          )}
        </div>

        <Grid className={classes.list} container direction='column'>
          {listData.map(info => (
            <Grid
              container
              item
              justifyContent='space-between'
              key={info.label}
              data-test={info.dataTest}>
              <div className='text-secondary'>{info.label}</div>

              {loading ? (
                <Skeleton variant='rectangular' height='12px' width='40px' />
              ) : (
                <div>{isNil(info.value) ? DISPLAY.empty : info.value}</div>
              )}
            </Grid>
          ))}
        </Grid>
      </div>
    )
  }
)
